import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2, Plus } from 'lucide-react';
import { apiFetch } from '../lib/api';
import { notify } from '../lib/notify';
import { Modal } from '../components/Modal';
import { createTicketSchema, type CreateTicketInput } from '../lib/schemas';

interface Category {
  id: number;
  slug: string;
  name: string;
}

interface CreatedTicket {
  id: number;
  code: string;
}

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: (ticket: CreatedTicket) => void;
}

export function CreateTicketModal({ open, onClose, onCreated }: Props) {
  const [categories, setCategories] = useState<Category[] | null>(null);
  const [loadingCats, setLoadingCats] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateTicketInput>({
    resolver: zodResolver(createTicketSchema),
    defaultValues: {
      subject: '',
      description: '',
      priority: 'normal',
      category: '',
      customer_name: '',
      customer_email: '',
      customer_phone: '',
    },
  });

  useEffect(() => {
    if (!open || categories !== null) return;
    setLoadingCats(true);
    apiFetch<Category[]>('/categories')
      .then((list) => setCategories(list))
      .catch((err) => notify.apiError(err, 'No se pudieron cargar las categorías.'))
      .finally(() => setLoadingCats(false));
  }, [open, categories]);

  const handleClose = () => {
    if (isSubmitting) return;
    reset();
    onClose();
  };

  const onSubmit = async (values: CreateTicketInput) => {
    try {
      const payload = {
        subject: values.subject,
        description: values.description,
        priority: values.priority,
        category: values.category || undefined,
        customer: {
          name: values.customer_name,
          email: values.customer_email || undefined,
          phone: values.customer_phone || undefined,
        },
      };
      const ticket = await apiFetch<CreatedTicket>('/tickets', {
        method: 'POST',
        body: payload,
      });
      reset();
      onCreated(ticket);
    } catch (err) {
      notify.apiError(err, 'No se pudo crear el ticket.');
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Nuevo ticket"
      description="Crea un ticket manual desde el panel. El cliente quedará registrado si es nuevo."
      size="lg"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
        <div className="space-y-3">
          <Field
            label="Asunto"
            error={errors.subject?.message}
            input={
              <input
                type="text"
                placeholder="Resumen breve del problema o consulta"
                {...register('subject')}
                className={inputClass(!!errors.subject)}
              />
            }
          />
          <Field
            label="Descripción"
            hint="Será el primer mensaje del hilo."
            error={errors.description?.message}
            input={
              <textarea
                rows={4}
                placeholder="Detalle del problema, contexto, pasos para reproducir…"
                {...register('description')}
                className={`${inputClass(!!errors.description)} resize-y min-h-[100px]`}
              />
            }
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field
              label="Prioridad"
              error={errors.priority?.message}
              input={
                <select {...register('priority')} className={inputClass(!!errors.priority)}>
                  <option value="low">Baja</option>
                  <option value="normal">Normal</option>
                  <option value="high">Alta</option>
                  <option value="urgent">Urgente</option>
                </select>
              }
            />
            <Field
              label="Categoría"
              hint={loadingCats ? 'Cargando categorías…' : 'Opcional.'}
              error={errors.category?.message}
              input={
                <select
                  {...register('category')}
                  disabled={loadingCats}
                  className={inputClass(!!errors.category)}
                >
                  <option value="">Sin categoría</option>
                  {(categories ?? []).map((c) => (
                    <option key={c.id} value={c.slug}>
                      {c.name}
                    </option>
                  ))}
                </select>
              }
            />
          </div>
        </div>

        <div className="pt-2 border-t border-slate-100">
          <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
            Cliente
          </h3>
          <div className="space-y-3">
            <Field
              label="Nombre"
              error={errors.customer_name?.message}
              input={
                <input
                  type="text"
                  placeholder="Nombre completo del cliente"
                  {...register('customer_name')}
                  className={inputClass(!!errors.customer_name)}
                />
              }
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Field
                label="Correo electrónico"
                hint="Opcional, recomendado para responder por email."
                error={errors.customer_email?.message}
                input={
                  <input
                    type="email"
                    placeholder="cliente@ejemplo.com"
                    {...register('customer_email')}
                    className={inputClass(!!errors.customer_email)}
                  />
                }
              />
              <Field
                label="Teléfono"
                hint="Opcional."
                error={errors.customer_phone?.message}
                input={
                  <input
                    type="tel"
                    placeholder="+58 412 1234567"
                    {...register('customer_phone')}
                    className={inputClass(!!errors.customer_phone)}
                  />
                }
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-3 py-2 text-sm rounded-lg text-slate-600 hover:bg-slate-100 disabled:opacity-40"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40"
          >
            {isSubmitting ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
            Crear ticket
          </button>
        </div>
      </form>
    </Modal>
  );
}

function inputClass(hasError: boolean) {
  return `w-full px-3 py-2 text-sm rounded-lg border bg-white focus:outline-none focus:ring-2 ${
    hasError ? 'border-rose-300 focus:ring-rose-500' : 'border-slate-200 focus:ring-blue-500'
  }`;
}

function Field({
  label,
  hint,
  error,
  input,
}: {
  label: string;
  hint?: string;
  error?: string;
  input: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-600 mb-1">{label}</label>
      {input}
      {error ? (
        <p className="text-xs text-rose-600 mt-1">{error}</p>
      ) : hint ? (
        <p className="text-[11px] text-slate-400 mt-1">{hint}</p>
      ) : null}
    </div>
  );
}
